import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import styles from "../EventDashboardV2.module.scss"
import { IEvent } from "../../../components/Events/types"

interface EventBannerProps {
  event: IEvent
  eventId: string
  lastRefreshed: Date | null
}

const ArrowLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
)

function useRelativeTime(date: Date | null, t: TFunction) {
  const [label, setLabel] = useState("")
  const [isStale, setIsStale] = useState(false)

  useEffect(() => {
    if (!date) {
      setLabel(t("eventDashboardV2.banner.connecting"))
      setIsStale(true)
      return
    }

    const update = () => {
      const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

      if (seconds < 5) {
        setLabel(t("eventDashboardV2.banner.updatedJustNow"))
        setIsStale(false)
      } else if (seconds < 60) {
        setLabel(t("eventDashboardV2.banner.updatedSecondsAgo", { seconds }))
        setIsStale(seconds > 30)
      } else {
        const minutes = Math.floor(seconds / 60)
        setLabel(t("eventDashboardV2.banner.updatedMinutesAgo", { minutes }))
        setIsStale(true)
      }
    }

    update()
    const timer = setInterval(update, 5000)
    return () => clearInterval(timer)
  }, [date, t])

  return { label, isStale }
}

const EventBanner: React.FC<EventBannerProps> = ({ event, eventId, lastRefreshed }) => {
  const { t } = useTranslation()
  const { label, isStale } = useRelativeTime(lastRefreshed, t)

  return (
    <div className={styles.banner}>
      <img
        src={event.pattern || "/vector/no-foto.svg"}
        alt={event.name}
      />
      <Link to={`/${eventId}`} className={styles.bannerBackLink}>
        <ArrowLeftIcon />
        {t("eventDashboardV2.banner.backToEvent")}
      </Link>
      <span className={styles.bannerLiveBadge}>
        <span className={isStale ? styles.bannerStaleDot : styles.bannerLiveDot} />
        {label}
      </span>
    </div>
  )
}

export default EventBanner
